import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@betareader/design-system';
import styled from 'styled-components';
import Icon from '../AppIcon';

import { useCredits } from 'src/hooks/useCredits';
import OutOfCreditsModal from './OutOfCreditsModal';

const Chip = styled.details`
  position: relative;
  display: inline-block;
`;

const ChipLabel = styled.summary`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border: 1px solid var(--br-warning-border);
  border-radius: 999px;
  background: var(--br-warning-surface);
  color: var(--br-warning-text);
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker { display: none; }
  &:focus-visible { outline: 2px solid var(--br-focus); outline-offset: 2px; }
`;

const PopupBody = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
  max-width: min(300px, calc(100vw - 24px));
  padding: 14px;
  background: var(--br-surface);
  color: var(--br-text);
  border: 1px solid var(--br-border);
  border-radius: var(--br-card-radius);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.15);

  .plan-line {
    font-size: 0.85em;
    color: var(--br-muted);
  }
`;

type CreditBalanceChipProps = {
  // Optional route to the usage/credits page; falls back to no-op when absent.
  usageHref?: string;
};

const CreditBalanceChip: React.FC<CreditBalanceChipProps> = ({ usageHref }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { data: credits } = useCredits();
  const [modalOpen, setModalOpen] = useState(false);
  const chipRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!chipRef.current?.contains(event.target as Node)) {
        if (chipRef.current) chipRef.current.open = false;
      }
    };
    document.addEventListener('pointerdown', closeOnOutsidePress);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePress);
  }, []);

  const balance = credits?.balance ?? 0;

  const openModal = () => {
    if (chipRef.current) {
      chipRef.current.open = false;
      chipRef.current.querySelector('summary')?.focus();
    }
    setModalOpen(true);
  };

  return (
    <>
      <Chip ref={chipRef}>
        <ChipLabel data-testid="credit-balance-chip">
          <Icon name="lightning" aria-hidden="true" />
          {t('NCredits', '{{count}} credits', { count: balance })}
        </ChipLabel>
        <PopupBody>
          <strong>
            {t('NCredits', '{{count}} credits', { count: balance })}
          </strong>
          {credits?.resetDate && (
            <span className="plan-line">
              {credits?.productInternalId === 'free'
                ? t('FreePlan', 'Free plan')
                : t('PremiumPlan', 'Premium plan')}{' '}
              ·{' '}
              {t('ResetsOn', 'resets {{date}}', {
                date: new Date(credits.resetDate).toLocaleDateString(),
              })}{' '}
              · {credits?.monthlyCredits ?? 0}/{t('Month', 'mo')}
            </span>
          )}
          <Button size="small" onClick={openModal}>
            {t('BuyMoreCredits', 'Buy more credits')}
          </Button>
          {usageHref && (
            <Button size="small" variant="secondary" onClick={() => history.push(usageHref)}>
              {t('ViewUsageHistory', 'View usage history')}
            </Button>
          )}
        </PopupBody>
      </Chip>
      <OutOfCreditsModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default CreditBalanceChip;
